import React, { useState, useRef } from 'react';
import axios from 'axios';
import api from '../api/client';
import { API_URL } from '../config';
import GoogleSignInButton from './GoogleSignInButton';
import fard from '../assets/img/fard.png';

const LoginForm = () => {
    const [login, setLogin] = useState('');
    const [password, setPassword] = useState('');
    const [message, setMessage] = useState('');
    const passwordInputRef = useRef(null);
    const rememberRef = useRef(null);
    // Второй шаг входа: сервер вернул билет и ждёт код из приложения.
    const [challenge, setChallenge] = useState(null);
    const [code, setCode] = useState('');
    const [verifying, setVerifying] = useState(false);

    // Общий финал для обоих способов входа: сохранить пару токенов и увести
    // на первый доступный человеку раздел.
    const finishLogin = async (data) => {
        localStorage.setItem('token', data.token);
        // Epic 6: refresh-токен продлевает сессию сам (30 дней с галочкой
        // «Keep me signed in», сутки без неё).
        localStorage.setItem('refreshToken', data.refreshToken);
        let landing = '/employee'; // универсальный фолбэк: пустая страница с меню по правам
        try {
            const perms = await api.get('/api/me/permissions');
            const pages = perms.data.pages || {};
            if (pages['general']?.view) landing = '/general';
            else if (pages['dashboard']?.view) landing = '/dashboard';
        } catch (permErr) {
            console.error('Failed to load permissions for redirect:', permErr);
        }
        window.location.href = landing;
    };

    const rememberMe = () => (rememberRef.current ? rememberRef.current.checked : true);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setMessage('');
        try {
            const res = await api.post(`/api/login`, { login, password, rememberMe: rememberMe() });
            if (res.data.twoFactorRequired) {
                setChallenge(res.data.challenge);
                setCode('');
                return;
            }
            await finishLogin(res.data);
        } catch (error) {
            console.error('Login error:', error);
            setMessage(error.response?.data?.message || 'Error logging in');
        }
    };

    const handleVerifyCode = async (e) => {
        e.preventDefault();
        setMessage('');
        setVerifying(true);
        try {
            const res = await api.post('/api/login/2fa', { challenge, code });
            await finishLogin(res.data);
        } catch (error) {
            const data = error.response?.data;
            setMessage(data?.message || 'Wrong code');
            // Билет протух — возвращаем человека к вводу пароля.
            if (error.response?.status === 401 && data?.message?.includes('expired')) {
                setChallenge(null);
            }
        } finally {
            setVerifying(false);
        }
    };

    // Вход через Google — та же учётная запись, сопоставление по почте.
    // Заголовок авторизации не шлём: старый токен в браузере мог бы получить
    // 401 от проверки сессий и увести человека в общий обработчик ошибок.
    const handleGoogleCredential = async (credential) => {
        setMessage('');
        try {
            const res = await axios.post(`${API_URL}/api/login/google`,
                { credential, rememberMe: rememberMe() });
            await finishLogin(res.data);
        } catch (error) {
            const data = error.response?.data;
            setMessage(data?.noSuchAccount
                ? 'There is no account with this email yet — use the request link below.'
                : (data?.message || 'Google sign-in failed'));
        }
    };

    const showPassword = (e) => {
        e.preventDefault();
        const passwordInput = passwordInputRef.current;
        if (passwordInput.type === 'password') {
            passwordInput.type = 'text';
        } else {
            passwordInput.type = 'password';
        }
    };

    // Второй шаг: код из приложения-аутентификатора либо резервный код.
    if (challenge) {
        return (
            <div className="container">
                <div className="login">
                    <div className="login__inner">
                        <form onSubmit={handleVerifyCode}>
                            <div className="container__input">
                                <label className="label">Verification code</label>
                                <input
                                    type="text"
                                    inputMode="numeric"
                                    autoComplete="one-time-code"
                                    autoFocus
                                    placeholder="6 digits from your authenticator app"
                                    value={code}
                                    onChange={(e) => setCode(e.target.value)}
                                />
                            </div>
                            <p className="two-fa-hint">
                                Open your authenticator app and enter the current code.
                                Lost your phone? Use one of your backup codes.
                            </p>
                            <input type="submit" className="btn btn__big btn__green align-center mt-12"
                                disabled={verifying} value={verifying ? 'Checking…' : 'Confirm'} />
                            <p className="mt-15 text-center">
                                <button type="button" className="link-button"
                                    onClick={() => { setChallenge(null); setMessage(''); }}>
                                    Back to sign in
                                </button>
                            </p>
                            <div id="message">{message}</div>
                        </form>
                    </div>
                    <div className="illustration">
                        <img src={fard} alt="Gardener Illustration" className="illustration_img" />
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="container">
            <div className="login">
                <div className="login__inner">
                    <form id="loginForm" onSubmit={handleSubmit}>
                        <div className="container__input">
                            <label className="label">Email Address</label>
                            <input 
                                type="text" 
                                id="login" 
                                name="login" 
                                placeholder="example@mail.com"
                                value={login}
                                onChange={(e) => setLogin(e.target.value)}
                            />
                        </div>
                        <div className="container__input"> 
                            <div className="row space__between"> 
                                <label className="label">Password</label> 
                                <a className="link__green" href="/forgot-password">Forgot Password</a> 
                            </div>
                            <div className="password"> 
                                <input 
                                    id="password" 
                                    className="password__input" 
                                    name="password" 
                                    type="password" 
                                    placeholder="at least 8 characters"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    ref={passwordInputRef}
                                />
                                <a href="#" className="password-control" onClick={showPassword}></a> 
                            </div>
                        </div>
                        <label className="checkbox mt-12"> 
                            <input type="checkbox" className="checkbox__real" name="rememberme" defaultChecked={true} ref={rememberRef} />
                            <span className="checkbox__fake"></span>
                            <span className="checkbox__title">Keep me signed in</span>
                        </label>
                        <input type="submit" className="btn btn__big btn__green align-center mt-12" value="Login" />
                        <GoogleSignInButton onCredential={handleGoogleCredential} />
                        <p className="mt-15 text-center">Don't have an Account? <a className="link__green" href="/sign-up">Request access here</a></p>
                        <div id="message">{message}</div>
                    </form>
                </div>
                <div className="illustration"> 
                    <img src={fard} alt="Gardener Illustration" className="illustration_img" />
                </div>
            </div>
        </div>
    );
};

export default LoginForm;
